import React, { forwardRef } from "react";
import { Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

interface DateRangePickerProps {
  label?: string;
  startDate?: Date | undefined;
  endDate?: Date | undefined;
  onChange: (dates: [Date | null, Date | null]) => void;
  minDate?: Date; // Optional minimum date
  maxDate?: Date; // Optional maximum date
  disabled?: boolean;
  required?: boolean;
  error?: string;
  touched?: boolean;
  showResetIcon?: boolean;
  onReset?: () => void;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  label = "Select Date",
  startDate,
  endDate,
  onChange,
  minDate,
  maxDate,
  disabled = false,
  required = false,
  error,
  touched,
  showResetIcon = false,
  onReset,
}) => {
  const CustomInput = forwardRef<
    HTMLInputElement,
    { value: string; onClick: () => void }
  >(({ value, onClick }, ref) => (
    <div className="form-floating position-relative">
      <input
        type="text"
        className={`form-control ${touched && error ? "is-invalid" : ""}`}
        onClick={onClick}
        ref={ref}
        value={value}
        placeholder=" "
        readOnly
        id="datepicker-input"
        disabled={disabled}
      />
      <label>
        {label}
        {!disabled && required && (
          <span className="required" style={{ color: "red" }}>
            *
          </span>
        )}
      </label>

      {showResetIcon && !disabled && (
        <span
          className="material-icons material-symbols-outlined date_reset"
          onClick={(e) => {
            e.stopPropagation();
            onReset?.();
          }}
          style={{
            position: "absolute",
            right: "35px",
            top: "50%",
            transform: "translateY(-50%)",
            cursor: "pointer",
            color: "#6c757d",
          }}
        >
          cached
        </span>
      )}

      {
        <span
          className="material-icons material-symbols-outlined date_ic"
          onClick={!disabled ? onClick : undefined}
          style={{
            position: "absolute",
            right: "10px",
            top: "50%",
            transform: "translateY(-50%)",
            cursor: !disabled ? "pointer" : "not-allowed",
            color: "#6c757d",
          }}
        >
          calendar_month
        </span>
      }

      {touched && error && <div className="invalid-feedback">{error}</div>}
    </div>
  ));

  CustomInput.displayName = "CustomInput";

  return (
    <Form.Group>
      <DatePicker
        selected={startDate}
        onChange={(update) => onChange(update as [Date | null, Date | null])}
        startDate={startDate}
        endDate={endDate}
        selectsRange
        monthsShown={2}
        minDate={minDate}
        maxDate={maxDate}
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        customInput={React.createElement(CustomInput)}
        disabled={disabled}
      />
    </Form.Group>
  );
};

export default DateRangePicker;
